(() => {
  const root = document.documentElement;
  const AUTO_START_HOUR = 7;
  const AUTO_END_HOUR = 19;
  const OVERRIDE_KEY = 'muaazon_theme_override';
  const OVERRIDE_UNTIL_KEY = 'muaazon_theme_override_until';
  const LEGACY_KEYS = ['muaazon_theme_preference', 'muaazon_vision_theme', 'muaazon-color-scheme'];
  let boundaryTimer = 0;

  const normalize = value => (value === 'dark' || value === 'black') ? 'dark' : 'light';
  const current = () => normalize(root.dataset.colorScheme);

  const localTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local';
    } catch (error) {
      return 'local';
    }
  };

  const autoThemeFor = date => {
    const hour = date.getHours();
    return hour >= AUTO_START_HOUR && hour < AUTO_END_HOUR ? 'light' : 'dark';
  };

  const nextBoundary = date => {
    const next = new Date(date);
    const hour = date.getHours();
    if (hour >= AUTO_START_HOUR && hour < AUTO_END_HOUR) {
      next.setHours(AUTO_END_HOUR, 0, 0, 0);
    } else if (hour < AUTO_START_HOUR) {
      next.setHours(AUTO_START_HOUR, 0, 0, 0);
    } else {
      next.setDate(next.getDate() + 1);
      next.setHours(AUTO_START_HOUR, 0, 0, 0);
    }
    return next;
  };

  const readOverride = () => {
    try {
      const value = localStorage.getItem(OVERRIDE_KEY);
      const until = Number(localStorage.getItem(OVERRIDE_UNTIL_KEY) || 0);
      if ((value === 'light' || value === 'dark') && until > Date.now()) {
        return { theme: value, until };
      }
      localStorage.removeItem(OVERRIDE_KEY);
      localStorage.removeItem(OVERRIDE_UNTIL_KEY);
    } catch (error) {}
    return null;
  };

  const syncButtons = theme => {
    document.querySelectorAll('[data-mz-theme-choice]').forEach(btn => {
      const active = normalize(btn.dataset.mzThemeChoice) === theme;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.toggleAttribute('data-active', active);
    });
  };

  const updateThemeColor = theme => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.content = theme === 'dark' ? '#0b1016' : '#f7f9fb';
  };

  const apply = (value, source = 'auto', announce = true) => {
    const theme = normalize(value);
    const changed = current() !== theme;

    root.dataset.colorScheme = theme;
    root.dataset.mzTheme = theme === 'dark' ? 'black' : 'white';
    root.dataset.mzThemeSource = source;
    root.dataset.mzTimezone = localTimezone();
    root.style.colorScheme = theme;
    updateThemeColor(theme);
    syncButtons(theme);

    if (announce && changed) {
      document.dispatchEvent(new CustomEvent('muaazon:vision-theme', {
        detail: {
          theme,
          visionTheme: theme === 'dark' ? 'black' : 'white',
          source,
          timezone: localTimezone()
        }
      }));
    }
  };

  const clearLegacyPermanentPreference = () => {
    try {
      LEGACY_KEYS.forEach(key => localStorage.removeItem(key));
    } catch (error) {}
  };

  const applyAutoOrOverride = (announce = true) => {
    const override = readOverride();
    if (override) {
      apply(override.theme, 'manual', announce);
      return;
    }
    apply(autoThemeFor(new Date()), 'auto', announce);
  };

  const scheduleBoundary = () => {
    if (boundaryTimer) clearTimeout(boundaryTimer);
    const now = new Date();
    const boundary = nextBoundary(now);
    const delay = Math.max(1000, boundary.getTime() - now.getTime() + 250);
    boundaryTimer = window.setTimeout(() => {
      try {
        localStorage.removeItem(OVERRIDE_KEY);
        localStorage.removeItem(OVERRIDE_UNTIL_KEY);
      } catch (error) {}
      applyAutoOrOverride(true);
      scheduleBoundary();
    }, Math.min(delay, 2147483647));
  };

  const setTemporaryOverride = value => {
    const theme = normalize(value);
    const until = nextBoundary(new Date()).getTime();
    clearLegacyPermanentPreference();
    try {
      localStorage.setItem(OVERRIDE_KEY, theme);
      localStorage.setItem(OVERRIDE_UNTIL_KEY, String(until));
    } catch (error) {}
    apply(theme, 'manual', true);
    scheduleBoundary();
  };

  const makeToggle = () => {
    if (document.querySelector('[data-mz-global-theme-toggle]')) return;
    const actions = document.querySelector('.msg-actions');
    if (!actions) return;

    const wrap = document.createElement('div');
    wrap.className = 'mz-theme-toggle';
    wrap.dataset.mzGlobalThemeToggle = '';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Appearance');
    wrap.innerHTML = `<button class="mz-theme-toggle__button" type="button" data-mz-theme-choice="light" aria-label="Use white theme until the next automatic switch"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg><span class="mz-theme-toggle__label">White</span></button><button class="mz-theme-toggle__button" type="button" data-mz-theme-choice="dark" aria-label="Use black theme until the next automatic switch"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.5 15.2A8.5 8.5 0 0 1 8.8 3.5 8.5 8.5 0 1 0 20.5 15.2Z"/></svg><span class="mz-theme-toggle__label">Black</span></button>`;
    actions.insertBefore(wrap, actions.firstChild);
  };

  document.addEventListener('click', event => {
    const btn = event.target.closest?.('[data-mz-theme-choice]');
    if (!btn) return;
    event.preventDefault();
    setTemporaryOverride(btn.dataset.mzThemeChoice);
  });

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      applyAutoOrOverride(true);
      scheduleBoundary();
    }
  };

  const init = () => {
    makeToggle();
    clearLegacyPermanentPreference();
    applyAutoOrOverride(false);
    scheduleBoundary();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once:true });
  } else {
    init();
  }

  document.addEventListener('visibilitychange', handleVisibility);
  document.addEventListener('shopify:section:load', () => {
    makeToggle();
    syncButtons(current());
  });

  /* Re-evaluate periodically in case the device timezone changes while the page is open. */
  window.setInterval(() => {
    applyAutoOrOverride(true);
    scheduleBoundary();
  }, 60000);
})();
